import type { CaptureUploadSnapshot, PCMAudioManager } from '@/lib/pcmAudioManager';
import { apiRequest } from '@/lib/queryClient';
import { uploadCaptureAudio } from '@/lib/recordingAudio';

/** Audio remains in memory on a failed upload, so retries use the same offsets. */
export class InterpreterRecordingAudio {
  enabled = false;
  saved = false;
  rejected = false;
  private pending = new Map<string, CaptureUploadSnapshot>();
  private saving: Promise<void> | null = null;

  constructor(readonly id: string) {}

  get unsaved() {
    return this.pending.size > 0;
  }

  async prepare() {
    await this.flush();
    const response = await apiRequest('GET', '/api/interpreter/audio-settings');
    const settings = await response.json();
    if (typeof settings.store_audio_in_blob !== 'boolean') throw new Error('audio_policy_unavailable');
    this.enabled = settings.store_audio_in_blob;
  }

  capture(manager: PCMAudioManager | null) {
    if (!this.enabled || !manager) return;
    const snapshot = manager.getUploadSnapshot();
    if (snapshot.pcm.length) {
      this.pending.set(`${snapshot.captureEpochId}:${snapshot.captureStartSample}`, snapshot);
      manager.clearPCMData();
    }
  }

  flush(): Promise<void> {
    if (this.saving) return this.saving;
    this.saving = this.upload().finally(() => {
      this.saving = null;
    });
    return this.saving;
  }

  private async upload() {
    for (const [key, snapshot] of this.pending) {
      const response = await uploadCaptureAudio(this.id, snapshot, undefined, {
        signal: AbortSignal.timeout(120_000),
        kind: 'interpreter',
      });
      if ([400, 403, 404, 410, 413, 415, 422].includes(response.status)) {
        // These bytes cannot be accepted. Do not retain audio against policy,
        // or trap the transcript workflow in an endless upload retry loop.
        this.pending.clear();
        this.rejected = true;
        return;
      }
      if (!response.ok) throw new Error('audio_upload_failed');
      this.pending.delete(key);
      this.saved = true;
    }
  }
}
